// ---------------------------------------------------------------------------
// Shared floating-element utilities
//
// Positioning, dismiss handlers, and helpers shared by Popover, Select, and
// any future component that anchors a floating panel to a trigger element.
// ---------------------------------------------------------------------------

// ── Types ──────────────────────────────────────────────────────────────────

/**
 * Preferred placement of a floating element relative to its trigger.
 * The positioning engine will automatically flip to the opposite side
 * if there is not enough viewport space in the preferred direction.
 */
export type FloatingPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

/** Resolved axis: which side the floating element ends up on after flip */
export type ResolvedSide = 'top' | 'bottom' | 'left' | 'right';

/** Alignment along the cross axis */
export type Alignment = 'start' | 'center' | 'end';

/** Result of full 12-placement positioning (used by Popover) */
export interface FloatingPosition {
  top: number;
  left: number;
  resolvedSide: ResolvedSide;
  alignment: Alignment;
  transformOrigin: string;
}

/** Result of simple above/below dropdown positioning (used by Select) */
export interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  openAbove: boolean;
  transformOrigin: string;
}

// ── Constants ──────────────────────────────────────────────────────────────

/** Gap in px between trigger and floating element */
export const FLOATING_OFFSET = 8;

/** Minimum viewport margin to avoid edge clamping */
export const VIEWPORT_PADDING = 8;

// ── Positioning helpers ────────────────────────────────────────────────────

/**
 * Parse a placement string into its side + alignment components.
 *
 * @example parsePlacement('top-start') // { side: 'top', alignment: 'start' }
 * @example parsePlacement('bottom')    // { side: 'bottom', alignment: 'center' }
 */
export function parsePlacement(p: FloatingPlacement): {
  side: ResolvedSide;
  alignment: Alignment;
} {
  const [side, alignment] = p.split('-') as [ResolvedSide?, Alignment?];

  if (!side) {
    return { side: 'bottom', alignment: 'center' };
  }

  return { side, alignment: alignment ?? 'center' };
}

/**
 * Align a floating element along the cross axis relative to its trigger.
 */
export function alignOnAxis(
  triggerStart: number,
  triggerSize: number,
  floatingSize: number,
  alignment: Alignment
) {
  if (alignment === 'start') return triggerStart;
  if (alignment === 'end') return triggerStart + triggerSize - floatingSize;
  return triggerStart + triggerSize / 2 - floatingSize / 2;
}

// ── Full positioning engine ────────────────────────────────────────────────

/**
 * Space-aware positioning engine supporting all 12 placements.
 *
 * 1. Attempt the preferred side
 * 2. If not enough room, flip to the opposite side
 * 3. If neither side works, pick the one with more space
 * 4. Clamp cross-axis to stay within the viewport
 * 5. Compute transform-origin for entrance animation
 */
export function computePosition(
  triggerRect: DOMRect,
  floatingEl: HTMLElement,
  placement: FloatingPlacement,
  maxWidth: number
): FloatingPosition {
  const floatingRect = floatingEl.getBoundingClientRect();
  const pw = Math.min(floatingRect.width, maxWidth);
  const ph = floatingRect.height;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const parsed = parsePlacement(placement);
  let side = parsed.side;
  const alignment = parsed.alignment;

  // ── Flip logic ──────────────────────────────────────────────────

  const spaceTop = triggerRect.top - VIEWPORT_PADDING;
  const spaceBottom = vh - triggerRect.bottom - VIEWPORT_PADDING;
  const spaceLeft = triggerRect.left - VIEWPORT_PADDING;
  const spaceRight = vw - triggerRect.right - VIEWPORT_PADDING;

  const opposites: Record<ResolvedSide, ResolvedSide> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  };

  const fits = (s: ResolvedSide) => {
    if (s === 'top') return spaceTop >= ph + FLOATING_OFFSET;
    if (s === 'bottom') return spaceBottom >= ph + FLOATING_OFFSET;
    if (s === 'left') return spaceLeft >= pw + FLOATING_OFFSET;
    return spaceRight >= pw + FLOATING_OFFSET;
  };

  const spaceFor = (s: ResolvedSide) => {
    if (s === 'top') return spaceTop;
    if (s === 'bottom') return spaceBottom;
    if (s === 'left') return spaceLeft;
    return spaceRight;
  };

  if (!fits(side)) {
    const opp = opposites[side];
    if (fits(opp)) {
      side = opp;
    } else {
      side = spaceFor(side) >= spaceFor(opp) ? side : opp;
    }
  }

  // ── Compute top/left ────────────────────────────────────────────

  // eslint-disable-next-line no-useless-assignment -- defensive default
  let top = 0;
  // eslint-disable-next-line no-useless-assignment -- defensive default
  let left = 0;

  if (side === 'top') {
    top = triggerRect.top - ph - FLOATING_OFFSET;
    left = alignOnAxis(triggerRect.left, triggerRect.width, pw, alignment);
  } else if (side === 'bottom') {
    top = triggerRect.bottom + FLOATING_OFFSET;
    left = alignOnAxis(triggerRect.left, triggerRect.width, pw, alignment);
  } else if (side === 'left') {
    left = triggerRect.left - pw - FLOATING_OFFSET;
    top = alignOnAxis(triggerRect.top, triggerRect.height, ph, alignment);
  } else {
    left = triggerRect.right + FLOATING_OFFSET;
    top = alignOnAxis(triggerRect.top, triggerRect.height, ph, alignment);
  }

  // ── Clamp to viewport ──────────────────────────────────────────

  left = Math.max(VIEWPORT_PADDING, Math.min(left, vw - pw - VIEWPORT_PADDING));
  top = Math.max(VIEWPORT_PADDING, Math.min(top, vh - ph - VIEWPORT_PADDING));

  // ── Transform origin for entrance animation ────────────────────

  const originMap: Record<ResolvedSide, string> = {
    top: 'bottom center',
    bottom: 'top center',
    left: 'right center',
    right: 'left center',
  };

  return {
    top,
    left,
    resolvedSide: side,
    alignment,
    transformOrigin: originMap[side],
  };
}

// ── Simple dropdown positioning ────────────────────────────────────────────

/**
 * Simple above/below dropdown positioning for Select-style components.
 *
 * Matches trigger width, flips above when there is not enough space below.
 * Uses `position:fixed` coordinates.
 */
export function computeDropdownPosition(
  triggerRect: DOMRect,
  options?: { maxHeight?: number; offset?: number }
): DropdownPosition {
  const maxHeight = options?.maxHeight ?? 300;
  const offset = options?.offset ?? 4;

  const spaceBelow = window.innerHeight - triggerRect.bottom;
  const openAbove = spaceBelow < maxHeight + offset && triggerRect.top > spaceBelow;

  const top = openAbove ? triggerRect.top - offset : triggerRect.bottom + offset;
  const transformOrigin = openAbove ? 'bottom' : 'top';

  return {
    top,
    left: triggerRect.left,
    width: triggerRect.width,
    openAbove,
    transformOrigin,
  };
}

// ── Visibility helpers ──────────────────────────────────────────────────────

/**
 * Returns `true` when the trigger element is fully outside the viewport.
 * Used to implement Floating UI / Radix-style "hide when detached" behavior.
 */
export function isTriggerDetached(triggerEl: HTMLElement): boolean {
  const r = triggerEl.getBoundingClientRect();
  return (
    r.bottom <= 0 || r.top >= window.innerHeight || r.right <= 0 || r.left >= window.innerWidth
  );
}

// ── Floating ownership ─────────────────────────────────────────────────────

/**
 * Data attribute placed on portaled floating elements (Select dropdown,
 * Popover panel) whose value is the DOM `id` of the trigger element that
 * owns them.  This lets `installOutsideClickHandler` walk the ownership
 * chain and recognise clicks inside portaled children as "inside".
 */
export const FLOATING_TRIGGER_ATTR = 'data-floating-trigger-id';

/**
 * Returns `true` when `target` is logically "inside" one of the
 * `containers` — either by direct DOM containment **or** by being
 * inside a portaled floating element whose trigger lives inside a
 * container (recursively).
 *
 * This solves the composability problem where a Select dropdown is
 * portaled to `document.body` but logically belongs to a Popover body.
 */
function isInsideOwnershipChain(
  target: Node,
  containers: (HTMLElement | null)[],
  visited: Set<string> = new Set()
): boolean {
  // 1. Direct containment
  for (const el of containers) {
    if (el?.contains(target)) return true;
  }

  // 2. Walk up from target looking for a portaled floating container
  //    that declares its trigger via data-floating-trigger-id.
  let node: Node | null = target;
  while (node) {
    if (node instanceof HTMLElement && node.hasAttribute(FLOATING_TRIGGER_ATTR)) {
      const triggerId = node.getAttribute(FLOATING_TRIGGER_ATTR);
      if (triggerId && !visited.has(triggerId)) {
        visited.add(triggerId);
        const triggerEl = document.getElementById(triggerId);
        if (triggerEl && isInsideOwnershipChain(triggerEl, containers, visited)) {
          return true;
        }
      }
    }
    node = node.parentNode;
  }

  return false;
}

// ── Dismiss handlers ───────────────────────────────────────────────────────
// Each function installs event listeners and returns a cleanup function.

/**
 * Close the floating element when clicking outside of it and its trigger.
 *
 * Uses `mousedown` (not `click`) so the containment check runs before
 * any option's `click` handler — no race conditions.
 *
 * Follows the `data-floating-trigger-id` ownership chain so that clicks
 * inside portaled child floating elements (e.g. a Select dropdown inside
 * a Popover) are not treated as "outside".
 */
export function installOutsideClickHandler(
  containers: (HTMLElement | null)[],
  onDismiss: () => void
) {
  const handler = (e: MouseEvent) => {
    const target = e.target as Node;
    if (!target) return;
    if (isInsideOwnershipChain(target, containers)) return;
    onDismiss();
  };
  document.addEventListener('mousedown', handler);
  return () => document.removeEventListener('mousedown', handler);
}

/**
 * Close or reposition the floating element when an ancestor scrolls.
 *
 * Uses a capture-phase listener so we catch scroll on any ancestor.
 * Ignores scroll events originating from inside the floating element
 * (e.g. scrolling an options list inside a dropdown).
 *
 * `mode`:
 * - `'close'` — debounced close (good for Select dropdowns)
 * - `'reposition'` — rAF-throttled repositioning (good for sticky Popovers)
 *
 * When `triggerEl` is provided with `mode: 'reposition'`, the handler
 * automatically closes the floating element if the trigger scrolls
 * fully out of the viewport ("hide when detached" — same concept as
 * Floating UI's `hide` middleware and Radix's `hideWhenDetached`).
 */
export function installScrollHandler(options: {
  floatingEl: HTMLElement;
  mode: 'close' | 'reposition';
  onClose?: () => void;
  onReposition?: () => void;
  triggerEl?: HTMLElement | null;
  debounceMs?: number;
}) {
  const { floatingEl, mode, onClose, onReposition, triggerEl, debounceMs = 100 } = options;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let rafId: number | undefined;

  const handler = (event: Event) => {
    if (floatingEl.contains(event.target as Node)) return;

    if (mode === 'reposition' && onReposition) {
      // Hide when detached: if the trigger has scrolled fully out of
      // the viewport, close instead of repositioning to a disorienting
      // viewport-edge position with no visible anchor.
      if (triggerEl && isTriggerDetached(triggerEl)) {
        onClose?.();
        return;
      }
      if (rafId !== undefined) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => onReposition());
    } else if (mode === 'close' && onClose) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => onClose(), debounceMs);
    }
  };

  document.addEventListener('scroll', handler, true);

  return () => {
    document.removeEventListener('scroll', handler, true);
    if (timer) clearTimeout(timer);
    if (rafId !== undefined) cancelAnimationFrame(rafId);
  };
}

/**
 * Close the floating element when Escape is pressed.
 * Optionally refocuses the given element (e.g. the trigger).
 */
export function installEscapeHandler(onDismiss: () => void, refocusEl?: HTMLElement | null) {
  const handler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onDismiss();
      refocusEl?.focus();
    }
  };
  document.addEventListener('keydown', handler);
  return () => document.removeEventListener('keydown', handler);
}

/**
 * Reposition on content size changes using ResizeObserver.
 */
export function installResizeObserver(el: HTMLElement, onResize: () => void) {
  const observer = new ResizeObserver(() => onResize());
  observer.observe(el);
  return () => observer.disconnect();
}

/**
 * Reposition when the browser window is resized.
 */
export function installWindowResizeHandler(onResize: () => void) {
  const handler = () => {
    requestAnimationFrame(() => onResize());
  };
  window.addEventListener('resize', handler);
  return () => window.removeEventListener('resize', handler);
}
